/**
 * Contagem Pós-Contagem Service
 *
 * Handles all pós-contagem por cliente operations:
 * list, create, update, delete, import from Excel.
 */
import apiClient from '../apiClient';
import type {
  PosContagemCliente,
  PosContagemClienteInput,
  ImportPosContagemResponse,
} from './contagemTypes';

class ContagemPosContagemService {
  /**
   * List all pos-contagem records for a job (one per client)
   * GET /api/jobs/{job_id}/pos-contagem-clientes
   */
  async getPosContagemClientes(jobId: number): Promise<PosContagemCliente[]> {
    try {
      const response = await apiClient.get(`/jobs/${jobId}/pos-contagem-clientes`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao buscar pós-contagem');
    }
  }

  /**
   * Create a new pos-contagem record for a client
   * POST /api/jobs/{job_id}/pos-contagem-clientes
   */
  async createPosContagemCliente(
    jobId: number,
    data: PosContagemClienteInput
  ): Promise<{ success: boolean; message: string; data: PosContagemCliente }> {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/pos-contagem-clientes`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar pós-contagem');
    }
  }

  /**
   * Update a pos-contagem record
   * PATCH /api/jobs/{job_id}/pos-contagem-clientes/{item_id}
   */
  async updatePosContagemCliente(
    jobId: number,
    itemId: number,
    data: Partial<PosContagemClienteInput>
  ): Promise<{ success: boolean; message: string; data: PosContagemCliente }> {
    try {
      const response = await apiClient.patch(`/jobs/${jobId}/pos-contagem-clientes/${itemId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao atualizar pós-contagem');
    }
  }

  /**
   * Delete a pos-contagem record
   * DELETE /api/jobs/{job_id}/pos-contagem-clientes/{item_id}
   */
  async deletePosContagemCliente(
    jobId: number,
    itemId: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/pos-contagem-clientes/${itemId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao excluir pós-contagem');
    }
  }

  /**
   * Import pos-contagem data from Excel
   * POST /api/jobs/{job_id}/import-pos-contagem-excel
   */
  async importPosContagemExcel(jobId: number, file: File): Promise<ImportPosContagemResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/jobs/${jobId}/import-pos-contagem-excel`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
}

export default new ContagemPosContagemService();
